import type { ClubsAttack } from '../../../data/full/jongleur';
import { clamp } from '../../../shared/scalar';
import { ticksIntoBeat } from '../../../shared/tempo';
import type { GaugeDefinition } from '../../../data/types';
import { angleOf } from '../../../shared/angle';
import { distanceSquared } from '../../../shared/vec';
import type { AttackModule } from '../../attacks/types';
import { pullTrigger } from '../../attacks/trigger';
import { hurtEnemy } from '../../effects';
import { setGauge } from '../../gauges';
import { clearOfObstacles, touchesObstacle } from '../../obstacles';
import type { PlayerState, PropState, SimState } from '../../state';
import { statValue } from '../../stats';
import type { StepContext } from '../../systems/types';
import { flyBack, ownerOf } from '../../props';

export const CLUB = 'club';

export const clubs: AttackModule<ClubsAttack> = {
  reads: ['attackCooldownMul', 'damageMul', 'pierceAdd', 'projectileSpeedMul'],

  ammunition: true,

  step({ state }, player, attack, input) {
    const club = nextThrow(state, player, attack);
    if (
      !pullTrigger(player, input.fire && club !== undefined, attack.cooldownTicks) ||
      club === undefined
    ) {
      return;
    }
    club.phase = 'flying';
    club.x = club.prevX = player.x;
    club.y = club.prevY = player.y;
    const speed = statValue(player, 'projectileSpeedMul', attack.speed);
    club.vx = player.aim.x * speed;
    club.vy = player.aim.y * speed;
    club.damage =
      statValue(player, 'damageMul', attack.damage) * (club.boosted ? attack.onBeatMul : 1);
    club.ticksLeft = attack.rangeTicks;
    club.hitsLeft = statValue(player, 'pierceAdd', attack.pierce) + 1;
    club.hitIds = [];
    delete club.boosted;
    state.events.push({
      type: 'playerFired',
      playerId: player.id,
      x: player.x,
      y: player.y,
      angle: angleOf(player.aim),
    });
  },

  update(ctx) {
    const { state } = ctx;
    if (state.props?.some((prop) => prop.kind === CLUB && prop.phase === 'flying') === true) {
      ctx.enemyGrid.rebuild(state.enemies);
    }
    for (const club of state.props ?? []) {
      if (club.kind !== CLUB) {
        continue;
      }
      const owner = ownerOf(state, club);
      const attack = clubsOf(ctx, owner);
      switch (club.phase) {
        case 'flying':
          fly(ctx, club, attack);
          break;
        case 'grounded':
          lie(ctx, club, owner, attack);
          break;
        case 'returning':
          flyBack(club, owner, attack.returnSpeed);
          break;
        case 'held':
        case 'bouncing':
          break;
      }
    }
  },

  reach(attack, player) {
    return statValue(player, 'projectileSpeedMul', attack.speed) * attack.rangeTicks;
  },

  fetch(state, player) {
    const owned = clubsOfPlayer(state, player);
    if (owned.some((club) => club.phase === 'held')) {
      return undefined;
    }
    let nearest: PropState | undefined;
    for (const club of owned) {
      if (
        club.phase === 'grounded' &&
        (nearest === undefined || distanceSquared(club, player) < distanceSquared(nearest, player))
      ) {
        nearest = club;
      }
    }
    return nearest;
  },
};

export function clubsGauge(
  { state }: StepContext,
  player: PlayerState,
  gauge: GaugeDefinition,
): void {
  const owned = clubsOfPlayer(state, player);
  const held = owned.filter((club) => club.phase === 'held').length;
  setGauge(player, gauge, owned.length === 0 ? gauge.max : held);
}

export function clubsOfPlayer(state: SimState, player: Pick<PlayerState, 'id'>): PropState[] {
  return (state.props ?? []).filter((prop) => prop.kind === CLUB && prop.playerId === player.id);
}

function clubsOf(ctx: StepContext, owner: PlayerState): ClubsAttack {
  const attack = ctx.content.classes.get(owner.classId)?.attack;
  if (attack?.kind !== 'clubs') {
    throw new Error(`player ${String(owner.id)} of class "${owner.classId}" throws no club`);
  }
  return attack;
}

// The player's clubs are made the first time.
function nextThrow(
  state: SimState,
  player: PlayerState,
  attack: ClubsAttack,
): PropState | undefined {
  const props = (state.props ??= []);
  let owned = 0;
  let next: PropState | undefined;
  for (const prop of props) {
    if (prop.kind !== CLUB || prop.playerId !== player.id) {
      continue;
    }
    owned += 1;
    if (prop.phase === 'held' && (next === undefined || (prop.boosted && !next.boosted))) {
      next = prop;
    }
  }
  for (; owned < attack.clubs; owned++) {
    const club: PropState = {
      id: state.nextEntityId,
      kind: CLUB,
      playerId: player.id,
      phase: 'held',
      x: player.x,
      y: player.y,
      prevX: player.x,
      prevY: player.y,
      vx: 0,
      vy: 0,
      damage: 0,
      ticksLeft: 0,
      hitsLeft: 0,
    };
    state.nextEntityId += 1;
    props.push(club);
    next ??= club;
  }
  return next;
}

function fly(ctx: StepContext, club: PropState, attack: ClubsAttack): void {
  const { state, enemyGrid, obstacles } = ctx;
  const { width, height } = state.arena;
  const hitIds = (club.hitIds ??= []);
  // Steps no longer than its width, so that a fast club never skips a bad vibe or an obstacle.
  const steps = Math.max(
    1,
    Math.ceil(Math.sqrt(club.vx * club.vx + club.vy * club.vy) / (2 * attack.radius)),
  );
  let inArena = true;
  let blocked = false;
  for (let step = 0; step < steps && club.hitsLeft > 0 && inArena && !blocked; step++) {
    club.x += club.vx / steps;
    club.y += club.vy / steps;
    const found = enemyGrid.queryInListOrder(club.x, club.y, attack.radius);
    for (let i = 0; i < found && club.hitsLeft > 0; i++) {
      const enemy = state.enemies[enemyGrid.result(i)];
      if (enemy === undefined || enemy.hp <= 0 || hitIds.includes(enemy.id)) {
        continue;
      }
      hurtEnemy(state, enemy, club.damage, club.playerId, {
        x: club.prevX,
        y: club.prevY,
      });
      hitIds.push(enemy.id);
      club.hitsLeft -= 1;
    }
    inArena = club.x >= 0 && club.x <= width && club.y >= 0 && club.y <= height;
    blocked = touchesObstacle({ x: club.x, y: club.y, radius: attack.radius }, obstacles);
  }
  club.ticksLeft -= 1;
  if (club.hitsLeft <= 0 || club.ticksLeft <= 0 || !inArena || blocked) {
    const resting = clearOfObstacles(
      {
        x: clamp(club.x, attack.radius, width - attack.radius),
        y: clamp(club.y, attack.radius, height - attack.radius),
      },
      attack.radius,
      obstacles,
    );
    club.phase = 'grounded';
    club.x = resting.x;
    club.y = resting.y;
    club.vx = club.vy = 0;
    club.ticksLeft = attack.returnBars * ctx.tempo.ticksPerBar;
    delete club.hitIds;
  }
}

function lie(ctx: StepContext, club: PropState, owner: PlayerState, attack: ClubsAttack): void {
  const { state, tempo } = ctx;
  if (!owner.downed && distanceSquared(club, owner) <= attack.catchRadius * attack.catchRadius) {
    club.phase = 'held';
    const sinceBeat = ticksIntoBeat(state.tick, tempo);
    if (Math.min(sinceBeat, tempo.ticksPerBeat - sinceBeat) <= attack.catchOnBeatTicks) {
      club.boosted = true;
    }
    return;
  }
  club.ticksLeft -= 1;
  if (club.ticksLeft <= 0) {
    club.phase = 'returning';
  }
}
